import { CUSTOM_ELEMENTS_SCHEMA, Injector, NgModule } from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UrlsComponent } from './components/urls/urls.component';
import { ErrorPageContainerComponent } from './containers/error-page/error-page.container';
import { HealthContainerComponent } from './containers/health/health.container';
import { CheckInContainerComponent } from './containers/check-in/checkin.container';
import { CheckInComponent } from './components/check-in/checkin.component';
import { GuestListStatusComponent } from './components/guestliststatus/guestliststatus.component';
import { CheckinNotAvailableContainerComponent } from './containers/checkin-not-available/checkin-not-available.container';
import { APP_BASE_HREF, CommonModule } from '@angular/common';
import { PromptsModule } from './modules/prompts/prompts.module';
import { LandingPageModule } from './modules/landing-page/landing-page.module';
import { HealthCenterModule } from './modules/health-center/health-center.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { reducers, metaReducers } from './reducers';
import { CoreModule } from './modules/core/core.module';
import { AutoCompleteModule } from './components/autocomplete';
import { RclExcaliburModule } from '@rccl/rcl-excalibur';
import { OlciEffects } from './effects/olcieffects';
import { TrackingEffects } from './effects/trackingeffects';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { LoadingModule } from './modules/loading/loading.module';
import { httpInterceptorProviders } from './http-interceptors';
import { DEFAULT_TIMEOUT } from './tokens/tokens';
import { CONSTANTS, constants } from './constants/constants';
import { MOCKS, mocks } from './response-mocks';
import { MockLoginComponent } from './components/mocklogin/mocklogin.component';
import { ScriptInjectorComponent } from './components/script-injector/script-injector.component';
/**
 * @module TelemetryModule
 * @service TelemetryContextService
 * @description These are the imports for telemetry.
 * @export
 */
import {
  TelemetryModule,
  TelemetryContextService,
  TelemetryHttpClientModule,
} from '@rccl/telemetry-angular';
/**
 * @description This is the new way to configure Telemetry.
 */
import { TelemetryDefaultConfig } from './telemetry.configuration';
import { GraphQLModule } from './graphql.module';
import { AdvertisementModule } from './modules/advertisement/advertisement.module';

@NgModule({
  declarations: [
    // Global
    AppComponent,
    ErrorPageContainerComponent,
    // Pages
    HealthContainerComponent,
    // Components
    CheckInContainerComponent,
    CheckInComponent,
    CheckinNotAvailableContainerComponent,
    GuestListStatusComponent,
    UrlsComponent,
    MockLoginComponent,
    ScriptInjectorComponent,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    PromptsModule,
    LandingPageModule,
    HealthCenterModule,
    BrowserModule,
    BrowserAnimationsModule,
    CoreModule.forRoot(),
    AutoCompleteModule,
    RclExcaliburModule,
    AppRoutingModule,
    StoreModule.forRoot(reducers, { metaReducers }),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment?.production,
    }),
    EffectsModule.forRoot([OlciEffects, TrackingEffects]),
    StoreRouterConnectingModule.forRoot(),
    FormsModule,
    LoadingModule,
    ReactiveFormsModule,
    TelemetryHttpClientModule,
    TelemetryModule.forRoot(TelemetryDefaultConfig),
    GraphQLModule,
    AdvertisementModule,
  ],
  providers: [
    // Services
    Title,
    httpInterceptorProviders,
    // Telemetry
    TelemetryContextService,
    // Others
    { provide: DEFAULT_TIMEOUT, useValue: 45100 },
    { provide: APP_BASE_HREF, useValue: '/checkin' },
    { provide: CONSTANTS, useValue: constants },
    { provide: MOCKS, useValue: mocks },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  static readonly injector: Injector;
}
